import { request } from "./http";

export interface Sku {
  id: string;
  sku_code: string;
  name: string;
  price_cents: number;
  currency: string;
  billing_type: string;
  entitlement_kind: string;
  entitlement_ref: string | null;
  duration_days: number | null;
}

export interface Product {
  id: string;
  slug: string;
  kind: string;
  name: string;
  summary: string | null;
  description_md: string | null;
  status: string;
  metadata: Record<string, unknown>;
  skus: Sku[];
}

export interface OrderItem {
  sku_code: string;
  product_name: string;
  quantity: number;
  unit_price_cents: number;
}

export interface Order {
  id: string;
  status: "pending" | "paid" | "failed" | "refunded" | "canceled";
  currency: string;
  subtotal_cents: number;
  total_cents: number;
  processor_session_id: string | null;
  created_at: string | null;
  paid_at: string | null;
  items: OrderItem[];
}

export interface Entitlement {
  sku_code: string;
  entitlement_kind: string;
  entitlement_ref: string | null;
  status: string;
  granted_at: string | null;
  expires_at: string | null;
}

export interface CheckoutItem {
  sku_code: string;
  quantity: number;
}

export type PaymentMethod = "card" | "apple_pay" | "google_pay" | "paypal";

export interface ShopConfig {
  enabled: boolean;
  allow_dev_activation: boolean;
  methods: PaymentMethod[];
}

export const shop = {
  config: () => request<ShopConfig>("/api/v1/shop/config", { noAuth: true }),
  products: () => request<{ products: Product[] }>("/api/v1/shop/products", { noAuth: true }),
  product: (slug: string) =>
    request<Product>(`/api/v1/shop/products/${encodeURIComponent(slug)}`, { noAuth: true }),
  checkout: (items: CheckoutItem[], method: PaymentMethod = "card") =>
    request<{ order_id: string; checkout_url: string }>("/api/v1/shop/checkout", {
      body: { items, method },
    }),
  devActivate: (items: CheckoutItem[]) =>
    request<{ order_id: string; status: string }>("/api/v1/shop/dev/activate", {
      body: { items },
    }),
  orders: () => request<{ orders: Order[] }>("/api/v1/shop/orders"),
  orderBySession: (sessionId: string) =>
    request<Order>(`/api/v1/shop/orders/by-session/${encodeURIComponent(sessionId)}`),
  entitlements: () => request<{ entitlements: Entitlement[] }>("/api/v1/shop/entitlements"),
};
